"use client";

// Third Party
import { c as _c } from "react/compiler-runtime";
import React, { Dispatch, SetStateAction, useState, useEffect, useRef, useCallback } from "react";
import { usePathname, useRouter } from "next/navigation";
import { orderBy } from "lodash";
import { AlertTriangle, ShoppingCart, ChevronDown, ChevronUp, X } from "lucide-react";

// Hooks
import { useLockBodyScroll } from "@/utils/hooks/useLockBodyScroll";

// Components
import { Button } from "@/components/ui/button";

// Helpers & Types
import { CartItem } from "@/utils/cart";
interface VendorConflictModalProps {
  refreshCartOnNewCartSelection: boolean;
  pendingCartItems: CartItem[];
  currentCartItems: CartItem[];
  isBuyNowAction?: boolean;
  bulkProductsAdd: ({
    products
  }: {
    products: CartItem[];
  }) => void;
  setIsCartOpen: (isCartOpen: boolean) => void;
  setVendorConflictCartItems: Dispatch<SetStateAction<{
    pendingCartItems: CartItem[];
    currentCartItems: CartItem[];
    refreshCartOnNewCartSelection: boolean;
  }>>;
  setProducts: (products: CartItem[]) => void;
  onClose?: () => void;
}
const VendorConflictModal: React.FC<VendorConflictModalProps> = t0 => {
  const $ = _c(88);
  const {
    refreshCartOnNewCartSelection: t1,
    pendingCartItems: t2,
    currentCartItems: t3,
    isBuyNowAction: t4,
    bulkProductsAdd,
    setIsCartOpen,
    setVendorConflictCartItems,
    setProducts,
    onClose
  } = t0;
  const refreshCartOnNewCartSelection = t1 === undefined ? false : t1;
  let t5;
  if ($[0] !== t2) {
    t5 = t2 === undefined ? [] : t2;
    $[0] = t2;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const pendingCartItems = t5;
  let t6;
  if ($[2] !== t3) {
    t6 = t3 === undefined ? [] : t3;
    $[2] = t3;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const currentCartItems = t6;
  const isBuyNowAction = t4 === undefined ? false : t4;
  const [openAccordion, setOpenAccordion] = useState("current");
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = accordion => {
      setOpenAccordion(accordion);
    };
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const toggleAccordion = t7;
  const router = useRouter();
  const pathname = usePathname();
  useLockBodyScroll(true);
  const buyNowTimerRef = useRef(null);
  const redirectTimerRef = useRef(null);
  let t8;
  let t9;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => () => {
      if (buyNowTimerRef.current) {
        clearTimeout(buyNowTimerRef.current);
      }
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
    t9 = [];
    $[5] = t8;
    $[6] = t9;
  } else {
    t8 = $[5];
    t9 = $[6];
  }
  useEffect(t8, t9);
  let t10;
  if ($[7] !== currentCartItems || $[8] !== refreshCartOnNewCartSelection || $[9] !== setProducts || $[10] !== setVendorConflictCartItems) {
    t10 = () => {
      if (refreshCartOnNewCartSelection) {
        const timeStamp = new Date().getTime();
        const newItems = currentCartItems.map(item => ({
          ...item,
          timeStamp
        }));
        setProducts(newItems);
        localStorage.removeItem("tooffi_cart_items");
      }
      setVendorConflictCartItems({
        pendingCartItems: [],
        currentCartItems: [],
        refreshCartOnNewCartSelection: false
      });
    };
    $[7] = currentCartItems;
    $[8] = refreshCartOnNewCartSelection;
    $[9] = setProducts;
    $[10] = setVendorConflictCartItems;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const handleKeepExisting = t10;
  let t11;
  if ($[12] !== handleKeepExisting || $[13] !== onClose) {
    t11 = () => {
      if (onClose) {
        onClose();
      } else {
        handleKeepExisting();
      }
    };
    $[12] = handleKeepExisting;
    $[13] = onClose;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const handleClose = t11;
  let t12;
  let t13;
  if ($[15] !== handleClose) {
    t12 = () => {
      const handleKeyDown = e => {
        if (e.key === "Escape") {
          handleClose();
        }
      };
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    };
    t13 = [handleClose];
    $[15] = handleClose;
    $[16] = t12;
    $[17] = t13;
  } else {
    t12 = $[16];
    t13 = $[17];
  }
  useEffect(t12, t13);
  let t14;
  if ($[18] !== bulkProductsAdd || $[19] !== isBuyNowAction || $[20] !== pathname || $[21] !== pendingCartItems || $[22] !== refreshCartOnNewCartSelection || $[23] !== router || $[24] !== setIsCartOpen || $[25] !== setVendorConflictCartItems) {
    t14 = async () => {
      const timeStamp_0 = new Date().getTime();
      const newItems_0 = refreshCartOnNewCartSelection ? pendingCartItems : pendingCartItems.map(item_0 => ({
        ...item_0,
        timeStamp: timeStamp_0
      }));
      await bulkProductsAdd({
        products: newItems_0
      });
      setVendorConflictCartItems({
        pendingCartItems: [],
        currentCartItems: [],
        refreshCartOnNewCartSelection: false
      });
      if (pathname !== "/checkout") {
        setIsCartOpen(true);
      }
      if (isBuyNowAction) {
        buyNowTimerRef.current = setTimeout(() => {
          router.push("/checkout");
        }, 1000);
      }
      if (pendingCartItems.length > 0 && !["/checkout", `/${pendingCartItems[0]?.vendor_details?.slug}/${pendingCartItems[0]?.vendor_id}`].includes(pathname || "")) {
        redirectTimerRef.current = setTimeout(() => {
          router.push(`/${pendingCartItems[0]?.vendor_details?.slug}/${pendingCartItems[0]?.vendor_id}`);
        }, 1000);
      }
    };
    $[18] = bulkProductsAdd;
    $[19] = isBuyNowAction;
    $[20] = pathname;
    $[21] = pendingCartItems;
    $[22] = refreshCartOnNewCartSelection;
    $[23] = router;
    $[24] = setIsCartOpen;
    $[25] = setVendorConflictCartItems;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const handleUseNew = t14;
  let t15;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <X className="w-5 h-5" />;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== handleClose) {
    t16 = <Button type="button" variant="ghost" size="icon" onClick={handleClose} className="absolute top-4 right-4 h-8 w-8 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100" aria-label="Close">{t15}</Button>;
    $[28] = handleClose;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  let t18;
  let t19;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="flex items-center justify-center mb-4"><div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center"><AlertTriangle className="w-6 h-6 text-yellow-600" /></div></div>;
    t18 = <h3 className="text-lg font-semibold text-gray-900 text-center mb-3">Different Vendors Detected</h3>;
    t19 = <p className="text-gray-600 text-center mb-6">You have items from different vendors in your cart. Please choose which vendor to continue with:</p>;
    $[30] = t17;
    $[31] = t18;
    $[32] = t19;
  } else {
    t17 = $[30];
    t18 = $[31];
    t19 = $[32];
  }
  let t20;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => toggleAccordion("current");
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <ShoppingCart className="w-5 h-5 text-blue-600" />;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== currentCartItems.length) {
    t22 = <span className="font-medium text-gray-900">Current Cart ({currentCartItems.length} items)</span>;
    $[35] = currentCartItems.length;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  const t23 = currentCartItems[0]?.vendor_name;
  let t24;
  if ($[37] !== t23) {
    t24 = <p className="text-sm text-gray-600">{t23}</p>;
    $[37] = t23;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== t22 || $[40] !== t24) {
    t25 = <div className="flex items-center space-x-3">{t21}<div>{t22}{t24}</div></div>;
    $[39] = t22;
    $[40] = t24;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  let t26;
  if ($[42] !== openAccordion) {
    t26 = openAccordion === "current" ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />;
    $[42] = openAccordion;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  let t27;
  if ($[44] !== t25 || $[45] !== t26) {
    t27 = <button onClick={t20} className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-100 transition-colors">{t25}{t26}</button>;
    $[44] = t25;
    $[45] = t26;
    $[46] = t27;
  } else {
    t27 = $[46];
  }
  let t28;
  if ($[47] !== currentCartItems || $[48] !== openAccordion) {
    t28 = openAccordion === "current" && <div className="px-4 pb-4 border-t border-gray-200 animate-in slide-in-from-top-2 duration-200"><div className="pt-3 space-y-2">{currentCartItems.length > 0 ? orderBy(currentCartItems, ["uniqueId"], ["asc"]).map(_temp) : <p className="text-sm text-gray-500 italic text-center py-2">No items in current cart</p>}</div></div>;
    $[47] = currentCartItems;
    $[48] = openAccordion;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  if ($[50] !== t27 || $[51] !== t28) {
    t29 = <div className="border border-gray-200 rounded-lg bg-gray-50">{t27}{t28}</div>;
    $[50] = t27;
    $[51] = t28;
    $[52] = t29;
  } else {
    t29 = $[52];
  }
  let t30;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = () => toggleAccordion("new");
    $[53] = t30;
  } else {
    t30 = $[53];
  }
  let t31;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <ShoppingCart className="w-5 h-5 text-green-600" />;
    $[54] = t31;
  } else {
    t31 = $[54];
  }
  let t32;
  if ($[55] !== pendingCartItems.length) {
    t32 = <span className="font-medium text-gray-900">New Items ({pendingCartItems.length} items)</span>;
    $[55] = pendingCartItems.length;
    $[56] = t32;
  } else {
    t32 = $[56];
  }
  const t33 = pendingCartItems[0]?.vendor_name;
  let t34;
  if ($[57] !== t33) {
    t34 = <p className="text-sm text-gray-600">{t33}</p>;
    $[57] = t33;
    $[58] = t34;
  } else {
    t34 = $[58];
  }
  let t35;
  if ($[59] !== t32 || $[60] !== t34) {
    t35 = <div className="flex items-center space-x-3">{t31}<div>{t32}{t34}</div></div>;
    $[59] = t32;
    $[60] = t34;
    $[61] = t35;
  } else {
    t35 = $[61];
  }
  let t36;
  if ($[62] !== openAccordion) {
    t36 = openAccordion === "new" ? <ChevronUp className="w-5 h-5 text-gray-500" /> : <ChevronDown className="w-5 h-5 text-gray-500" />;
    $[62] = openAccordion;
    $[63] = t36;
  } else {
    t36 = $[63];
  }
  let t37;
  if ($[64] !== t35 || $[65] !== t36) {
    t37 = <button onClick={t30} className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-100 transition-colors">{t35}{t36}</button>;
    $[64] = t35;
    $[65] = t36;
    $[66] = t37;
  } else {
    t37 = $[66];
  }
  let t38;
  if ($[67] !== openAccordion || $[68] !== pendingCartItems) {
    t38 = openAccordion === "new" && <div className="px-4 pb-4 border-t border-gray-200 animate-in slide-in-from-top-2 duration-200"><div className="pt-3 space-y-2">{pendingCartItems.length > 0 ? orderBy(pendingCartItems, ["uniqueId"], ["asc"]).map(_temp2) : <p className="text-sm text-gray-500 italic text-center py-2">No new items to add</p>}</div></div>;
    $[67] = openAccordion;
    $[68] = pendingCartItems;
    $[69] = t38;
  } else {
    t38 = $[69];
  }
  let t39;
  if ($[70] !== t37 || $[71] !== t38) {
    t39 = <div className="border border-gray-200 rounded-lg bg-gray-50">{t37}{t38}</div>;
    $[70] = t37;
    $[71] = t38;
    $[72] = t39;
  } else {
    t39 = $[72];
  }
  let t40;
  if ($[73] !== t29 || $[74] !== t39) {
    t40 = <div className="space-y-4">{t29}{t39}</div>;
    $[73] = t29;
    $[74] = t39;
    $[75] = t40;
  } else {
    t40 = $[75];
  }
  let t41;
  if ($[76] !== handleKeepExisting) {
    t41 = <Button onClick={handleKeepExisting} variant="outline" className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50">Keep Current Cart</Button>;
    $[76] = handleKeepExisting;
    $[77] = t41;
  } else {
    t41 = $[77];
  }
  let t42;
  if ($[78] !== handleUseNew) {
    t42 = <Button onClick={handleUseNew} className="flex-1 bg-[#037a6a] hover:bg-[#229081] text-white">Use New Items</Button>;
    $[78] = handleUseNew;
    $[79] = t42;
  } else {
    t42 = $[79];
  }
  let t43;
  if ($[80] !== t41 || $[81] !== t42) {
    t43 = <div className="flex space-x-3 mt-6">{t41}{t42}</div>;
    $[80] = t41;
    $[81] = t42;
    $[82] = t43;
  } else {
    t43 = $[82];
  }
  let t44;
  if ($[83] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = <p className="text-xs text-gray-500 text-center mt-4">Note: Choosing one vendor will clear items from the other vendor</p>;
    $[83] = t44;
  } else {
    t44 = $[83];
  }
  let t45;
  if ($[84] !== t16 || $[85] !== t40 || $[86] !== t43) {
    t45 = <div className="fixed inset-0 bg-black/50 z-[10001] flex items-center justify-center p-4 touch-none"><div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl overscroll-contain touch-auto relative">{t16}{t17}{t18}{t19}{t40}{t43}{t44}</div></div>;
    $[84] = t16;
    $[85] = t40;
    $[86] = t43;
    $[87] = t45;
  } else {
    t45 = $[87];
  }
  return t45;
};
export default VendorConflictModal;
function _temp(item_1, index) {
  return <div key={index} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 shadow-sm"><div className="flex-1"><p className="text-sm font-medium text-gray-900">{item_1.name}</p><p className="text-xs text-gray-500">Qty: {item_1.count}</p></div><p className="text-sm font-medium text-gray-900">₹{item_1.final_price_calculated}</p></div>;
}
function _temp2(item_2, index_0) {
  return <div key={index_0} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 shadow-sm"><div className="flex-1"><p className="text-sm font-medium text-gray-900">{item_2.name}</p><p className="text-xs text-gray-500">Qty: {item_2.count}</p></div><p className="text-sm font-medium text-gray-900">₹{item_2.final_price_calculated}</p></div>;
}